import { Dispatch } from "react";
import { Action } from "redux";
import { CreateScheduleSlice } from "../CreateSchedule/redux";
import { IInitialState, ScheduleDetails, IScheduleRecurrenceData } from "../CreateSchedule/redux/Types";
import { getEndByDateDefaultValue } from "../SchedulingEntries/ScheduleValidation";
import { dayRadioOption, RecursTypes } from "../SchedulingEntries/Types";
import { DEFAULT_RECCURENCE_TIME } from "./Constants";

/**
 * Converts Date object to string in dd-mm-yyyy format
 * @param date - Contains the date to be formatted
 * @param isMonthInAphabet - Flag value to format the month type
 * @returns - Returns the dd-mm-yy format as string
 */
export const getFormattedDate = (date: Date, isMonthInAphabet: boolean, isYearFirst?: boolean): string => {
  const monthType = (isMonthInAphabet) ? "long" : "2-digit";
  const day = date?.toLocaleString("default", { day: "2-digit" });
  const month = date?.toLocaleString("default", { month: monthType });
  const year = date?.toLocaleString("default", { year: "numeric" });
  return isYearFirst ? (year + "-" + month + "-" + day) : (day + "-" + month + "-" + year);
};

/**
 * Creates the custom end date for recurrence type weekly, monthly, quaterly and yearly
 * @param schedule - Contains the schedule details
 * @param type - Contains either daily, weekly , monthly, quarterly or yearly
 * @returns - Returns the date based on the recur type
 */

export const getCustomRecurrenceEndDate = (schedule: ScheduleDetails, type: string): string => {
  return getFormattedDate(new Date(getEndByDateDefaultValue(schedule.recurrenceRangeStart, type)), false);
};

/**
 * Generates default schedule data for recurrence type weekly, monthly, quaterly and yearly
 * @param type - Contains either daily, weekly , monthly, quarterly or yearly
 * @param schedule - Contains the schedule details 
 * @returns - Returns the schedule details
 */
export const GetDefaultScheduleData = (type: string, schedule: ScheduleDetails): ScheduleDetails => {
  const data: ScheduleDetails = {
    activeInLive: schedule.activeInLive,
    scheduleName: schedule.scheduleName,
    recurrenceType: type,
    recurrenceRunTime: DEFAULT_RECCURENCE_TIME,
    recurrenceRangeStart: (schedule.recurrenceRangeStart) ? schedule.recurrenceRangeStart :
      getFormattedDate(new Date(), false),
    recurrenceRangeEnd: schedule.recurrenceRangeEnd ? getCustomRecurrenceEndDate(schedule, type) : undefined,
    scheduleGuid: schedule.scheduleGuid
  };
  if (type === RecursTypes.Weekly) {
    data.recurrenceData = {
      selectedWeekDays: [0]
    };
  }
  else if (type === RecursTypes.Monthly) {
    data.recurrenceData = {
      day: 1
    };
  }
  else if (type === RecursTypes.Quarterly ||
    type === RecursTypes.Yearly) {
    data.recurrenceData = {
      day: 1,
      month: 1
    };
  }
  if (schedule.recurrenceNoOfOccurrences) {
    data.recurrenceNoOfOccurrences = schedule.recurrenceNoOfOccurrences;
  }
  return data;
};

/**
 * Generates week days default values for day & calculation field combination in redux
 * @param recurrenceType - Contains either daily, weekly , monthly, quarterly or yearly
 * @param data - Contains the recurrence data
 * @param selectedValue - Contains the selected value by the user
 * @returns - Returns the schedule recurrence data
 */
export const getDayWeekDefaultValues = (recurrenceType: string, data: IScheduleRecurrenceData | undefined,
  selectedValue: string | number | string[]): IScheduleRecurrenceData => {
  const defaultRecurData = {} as IScheduleRecurrenceData;
  if (selectedValue === dayRadioOption[0].value) {
    defaultRecurData.day = (data && data.day) ? data.day : 1;
  } else {
    defaultRecurData.calculation = (data && data.calculation) ? data.calculation : [0, 0];
  }
  if ((recurrenceType === "monthly" || recurrenceType === "quarterly" || recurrenceType === "yearly")
    && (data && data.month)) {
    defaultRecurData.month = data.month;
  }
  return defaultRecurData;
};

/**
 * Converts date of string type to Date type
 * @param date - Contains the selected date
 * @returns - Returns the date by removing the time details
 */
export const convertDateToView = (date: string): Date => {
  const splittedDate = date.split(" ");
  if (splittedDate.length > 0) {
    date = splittedDate[0];
  }
  const result = date.split("-").reverse().join("-");
  return new Date(result);
};

/**
 * Returns time in hours:minutes format of string type
 * @param time - Contains the current time value
 * @returns - Returns the hours:minutes time format
 */
export const convertTimeToView = (time: string): string => {
  return time.split(":")[0] + ":" + time.split(":")[1];
};
/**
 * Set the selected start value & end value in redux, if endby is selected
 * else start date alone is dispatched
 * @param scheduling - Contains the scheduling details
 * @param dispatch - Object to dispatch the state values
 * @param validationText - Contains the validated text
 * @param selectedDate - Contains the selected date value
 * @param endRecurrenceDate - end date for the schedule
 * @returns - Dispatches the start and end date
 */
export const setStartAndEndDate = (
  scheduling: IInitialState,
  dispatch: Dispatch<Action>,
  validationText: string,
  selectedDate: Date,
  endRecurrenceDate: string,
): void => {
  if (!scheduling.isEndBy) {
    dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
      ...scheduling.schedulingDetails,
      recurrenceRangeStart: (validationText === "") ?
        getFormattedDate(selectedDate, false) : "",
      recurrenceRangeEnd: endRecurrenceDate
    }));
  }
  else {
    dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
      ...scheduling.schedulingDetails,
      recurrenceRangeStart: (validationText === "") ?
        getFormattedDate(selectedDate, false) : ""
    }));
  }
};

/**
 * Get Valid Date Format for api
 * @param value Date
 * @returns string
 */
export const GetValidDateFormat = (value?: Date): string => {
  let returnValue = "";
  if (value) {
    const date = value;
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    returnValue = (year + "-" + month + "-" + day + " " + hours + ":" + minutes + ":" + seconds);
  }
  return returnValue;
};

/**
 * Method to display the date in UI as per emis ui input type date format
 * @param recurrenceDate - current recurrence date
 * @returns in "yyyy-mm-dd" format.
 * Note: This method is written for temporary purpose will be removed when emis ui datepicker is migrated
 */
export const getCustomInputDateInView = (recurrenceDate: string): string => {
  const recurDate = new Date(recurrenceDate);
  const recurMonth = recurDate.toLocaleString("default", { month: "2-digit" });
  const customDate = (recurDate.getFullYear()+"-"+recurMonth+"-"+recurDate.getDate().toString().padStart(2, "0"));
  return customDate;
};

/**
 * Method to validate if selected date is lesser than 8 years
 * @param recurrenceDate - currently selected date (recurrence start date / recurrence end date)
 * @returns true - if selected date is greater than 8 years, false - if selected date is lesser than 8 years 
 * Note: This method is written for temporary purpose will be removed when emis ui datepicker is migrated
 */
export const getCustomMaxDateValidation = (recurrenceDate: string | Date | undefined): boolean => {
  if(recurrenceDate !== undefined) {
    const recurDate = new Date(recurrenceDate);
    const selectedDate = recurDate.getDate();
    const selectedMonth = recurDate.getMonth()+1;
    const selectedYear = recurDate.getFullYear();
    const maxYear = new Date().getFullYear()+8;
    const maxMonth = new Date().getMonth()+1;
    const maxDate = new Date().getDate();
    if(selectedYear >= maxYear) {
      if(selectedMonth >= maxMonth) {
        if(selectedDate > maxDate) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
};
